/**
 * The update-required signal's listener (§R.121, §R.123, §R.127).
 *
 * supabase-js RESOLVES with `{ data, error }` rather than throwing, and every
 * page is reused as-is, so nothing would ever look at a server refusal. This
 * wrapper is handed to `createClient` as `global.fetch`, sees every PostgREST
 * response whatever the call site, and raises the blocking screen when the
 * body carries the reserved SQLSTATE.
 *
 * `KA001` is reserved for this and verified unused anywhere else in the
 * repository. Never match a code the application already uses — `55000`,
 * `P0002`, `42501`, `23514` are ordinary failures, and matching them would
 * force an update screen on every one.
 *
 * It ships INERT: no RPC returns `KA001` until the post-M7 retirement
 * migration. It ships in the first distributed build because an installed
 * binary cannot be given a listener later.
 */
import { raiseUpdateRequired } from '@/mobile/versionGuard';

export const UPDATE_REQUIRED_SQLSTATE = 'KA001';

type FetchLike = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;

/** Reads the SQLSTATE out of a PostgREST error body, or null. Never throws. */
export async function extractSqlState(res: Response): Promise<string | null> {
  try {
    const text = await res.clone().text();
    if (!text) return null;
    const body: unknown = JSON.parse(text);
    if (body && typeof body === 'object' && 'code' in body) {
      const code = (body as { code?: unknown }).code;
      return typeof code === 'string' ? code : null;
    }
    return null;
  } catch {
    return null;
  }
}

export function createUpdateRequiredFetch(
  base?: FetchLike,
  onRefused: () => void = () => raiseUpdateRequired('server-refused'),
): FetchLike {
  return async (input, init) => {
    // Resolve the real fetch at call time so environments that replace
    // globalThis.fetch later (tests, polyfills) are still honoured.
    const doFetch: FetchLike = base ?? ((i, o) => globalThis.fetch(i, o));
    const res = await doFetch(input, init);
    if (!res.ok) {
      const code = await extractSqlState(res);
      if (code === UPDATE_REQUIRED_SQLSTATE) onRefused();
    }
    return res;
  };
}
